import type { AutomationLogLineDto, AutomationRunResultsDto } from '@tms/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Clock,
  Cpu,
  Download,
  ExternalLink,
  FileWarning,
  GitBranch,
  RotateCcw,
  Server,
  XCircle,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '../../../components/layout/PageHeader';
import {
  Badge,
  Button,
  buttonClassName,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  ErrorState,
  ProgressBar,
  SkeletonRows,
} from '../../../components/ui';
import { useFormat, useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { runReportHref, useAutomationCan, useCancelRun, useRerunFailed, useRun } from '../hooks';
import { AutomationStatusBadge, FailureCauses, StageIcon } from '../ui';
import { useRunsErrorText } from './format';
import { useLiveRunResults } from './hooks';
import { ClassesCard, ComparisonCard, ResultsSummary } from './RunResults';

/**
 * One run: its stages, the job log, the results from GitLab's test report with
 * the comparison to an earlier run, cancel and «rerun failed»
 * (TestRun src/pages/RunPage.tsx).
 */
export function RunPage({ runId, projectHint }: { runId: string; projectHint?: string }) {
  const { t } = useT();
  const { dateTime } = useFormat();
  const navigate = useNavigate();
  const errorText = useRunsErrorText();
  const can = useAutomationCan();
  // polls while the run goes and stops by itself when it settles
  const run = useRun(runId, projectHint);
  const current = run.data;
  const isLive = current?.status === 'running' || current?.status === 'pending';
  // the run picked by hand to compare with; without one the server picks the closest
  const [against, setAgainst] = useState<string | undefined>(undefined);
  const results = useLiveRunResults(runId, {
    ...(projectHint ? { projectHint } : {}),
    ...(against ? { against } : {}),
    final: Boolean(current) && !isLive,
    enabled: Boolean(current),
  });
  const cancel = useCancelRun();
  const rerun = useRerunFailed();
  const [cancelOpen, setCancelOpen] = useState(false);

  if (run.error) {
    return (
      <Card>
        <ErrorState error={run.error} onRetry={() => void run.refetch()} />
      </Card>
    );
  }
  if (!current) return <SkeletonRows rows={5} className="p-0" />;

  const r = results.data;
  const done = !isLive;
  const redCount = r ? r.summary.failed + r.summary.broken : 0;
  // only for runs started from here: a pipeline started by a push has no run settings
  const canRerunFailed = can.run && done && current.trigger === 'admin' && redCount > 0;

  const onRerun = () =>
    rerun.mutate(
      { runId: current.id },
      {
        onSuccess: (started) => {
          toast.success(t('automation.runs.rerunStarted'));
          void navigate({
            to: '/automation/runs/$runId',
            params: { runId: started.pipelineId },
            search: { project: started.projectId },
          });
        },
        onError: (error) => toast.error(errorText(error)),
      },
    );

  const onCancel = () =>
    cancel.mutate(current.id, {
      onSuccess: () => {
        setCancelOpen(false);
        toast.warning(t('automation.runs.cancelled'));
      },
      onError: (error) => {
        setCancelOpen(false);
        toast.error(errorText(error));
      },
    });

  const link = (href: string | undefined, label: string, primary = false, title?: string) =>
    href ? (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        title={title}
        className={buttonClassName(primary ? 'primary' : 'secondary', 'sm')}
      >
        <ExternalLink className="size-3.5" aria-hidden="true" />
        {label}
      </a>
    ) : null;

  return (
    <>
      <div>
        <Link
          to="/automation/teams/$teamId/projects/$projectId"
          params={{ teamId: current.teamId, projectId: current.projectId }}
          search={{ tab: 'dashboard' }}
          className={buttonClassName('ghost', 'sm', '-ml-2.5')}
        >
          <ArrowLeft className="size-3.5" aria-hidden="true" />
          {t('automation.runs.backToProject')}
        </Link>
      </div>

      <PageHeader
        title={
          <span className="font-mono">{t('automation.runs.runTitle', { id: current.id })}</span>
        }
        meta={
          <>
            <AutomationStatusBadge status={current.status} />
            <Badge>{current.projectName}</Badge>
          </>
        }
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {canRerunFailed ? (
              <Button
                size="sm"
                onClick={onRerun}
                loading={rerun.isPending}
                title={t('automation.runs.rerunFailedHint')}
              >
                <RotateCcw className="size-3.5" aria-hidden="true" />
                {t('automation.runs.rerunFailed')}
              </Button>
            ) : null}
            {isLive && can.run ? (
              <Button
                size="sm"
                className="border-fail/40 text-fail hover:bg-fail-soft"
                onClick={() => setCancelOpen(true)}
              >
                <XCircle className="size-3.5" aria-hidden="true" />
                {t('automation.runs.cancel')}
              </Button>
            ) : null}
            {!isLive ? (
              <a
                href={runReportHref(current.pipelineId, current.projectId)}
                download
                title={t('automation.runs.downloadReportHint')}
                className={buttonClassName('primary', 'sm')}
              >
                <Download className="size-3.5" aria-hidden="true" />
                {t('automation.runs.downloadReport')}
              </a>
            ) : null}
            {link(current.allureUrl, t('automation.runs.allure'))}
            {link(
              current.testsUrl,
              t('automation.runs.tests'),
              false,
              t('automation.runs.testsHint'),
            )}
            {link(
              current.reportUrl,
              t('automation.runs.report'),
              false,
              t('automation.runs.reportHint'),
            )}
            {link(current.pipelineUrl, t('automation.runs.pipeline'))}
          </div>
        }
      />

      <Card>
        <CardBody>
          <dl className="flex flex-wrap gap-x-6 gap-y-3 text-[13px]">
            <Meta
              icon={<GitBranch className="size-3.5" />}
              label={t('automation.runs.meta.pipeline')}
              value={current.pipelineId}
            />
            <Meta
              icon={<Cpu className="size-3.5" />}
              label={t('automation.runs.meta.runner')}
              value={current.runner}
            />
            <Meta
              icon={<Server className="size-3.5" />}
              label={t('automation.runs.meta.environment')}
              value={current.config.environment ?? '—'}
            />
            {current.config.testType ? (
              <Meta
                icon={<Cpu className="size-3.5" />}
                label={t('automation.runs.meta.testType')}
                value={current.config.testType}
              />
            ) : null}
            {current.config.testType === 'UI' && current.config.browser ? (
              <Meta
                icon={<Cpu className="size-3.5" />}
                label={t('automation.runs.meta.browser')}
                value={`${current.config.browser}${current.config.device ? ` · ${current.config.device}` : ''}`}
              />
            ) : null}
            <Meta
              icon={<GitBranch className="size-3.5" />}
              label={t('automation.runs.meta.branch')}
              value={current.branch}
            />
            <Meta
              icon={<Clock className="size-3.5" />}
              label={t('automation.runs.meta.started')}
              value={dateTime(current.startedAtISO || current.createdAt)}
            />
            {current.config.threads ? (
              <Meta
                icon={<Cpu className="size-3.5" />}
                label={t('automation.runs.meta.threads')}
                value={String(current.config.threads)}
              />
            ) : null}
          </dl>
          {current.config.command ? (
            <div className="mt-3 border-t border-border pt-3">
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">
                {t('automation.runs.meta.command')}
              </p>
              <code className="break-all font-mono text-[12.5px] text-accent-fg">
                {current.config.command}
              </code>
            </div>
          ) : null}
        </CardBody>
      </Card>

      {r?.ready ? (
        <>
          <ResultsSummary results={r} />
          <FailureCauses causes={r.causes} red={r.summary.failed + r.summary.broken} />
          {r.final ? (
            <ComparisonCard
              results={r}
              against={against}
              onAgainst={setAgainst}
              classNames={Object.fromEntries(r.classes.map((c) => [c.fqcn, c.displayName]))}
            />
          ) : null}
          <ClassesCard results={r} />
        </>
      ) : null}
      {done ? (
        <NoReport
          results={r}
          loading={results.isLoading || results.isFetching}
          error={results.error}
        />
      ) : null}

      {/* minmax(0, 1fr): long log lines scroll inside the log, not the page */}
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="flex min-w-0 flex-col gap-4">
          {!done ? (
            <Card>
              <CardBody className="space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-[14px] font-semibold text-ink">
                    {t('automation.runs.progress')}
                  </h2>
                  <span className="font-mono text-[14px] font-semibold">{current.progress}%</span>
                </div>
                <ProgressBar value={current.progress} />
              </CardBody>
            </Card>
          ) : null}
          <LogViewer logs={current.logs ?? []} live={isLive} />
        </div>

        <Card className="lg:sticky lg:top-4">
          <CardHeader title={t('automation.runs.stages')} />
          <CardBody>
            <ol>
              {current.stages.map((stage, index) => {
                const last = index === current.stages.length - 1;
                return (
                  <li key={stage.key} className={cn('relative flex gap-3', !last && 'pb-4')}>
                    {!last ? (
                      <span
                        aria-hidden="true"
                        className={cn(
                          'absolute bottom-0 left-[7px] top-5 w-0.5',
                          stage.status === 'done' ? 'bg-pass' : 'bg-border',
                        )}
                      />
                    ) : null}
                    <StageIcon status={stage.status} className="mt-0.5" />
                    <span className="flex flex-1 items-center justify-between gap-2">
                      <span
                        className={cn(
                          'text-[13px]',
                          stage.status === 'active' && 'font-semibold',
                          stage.status === 'pending' ? 'text-ink-subtle' : 'text-ink',
                        )}
                      >
                        {stage.label}
                      </span>
                      {stage.duration ? (
                        <span className="font-mono text-[12px] text-ink-subtle">
                          {stage.duration}
                        </span>
                      ) : null}
                    </span>
                  </li>
                );
              })}
            </ol>
          </CardBody>
        </Card>
      </div>

      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title={t('automation.runs.cancelTitle', { id: current.id })}
        description={t('automation.runs.cancelDescription')}
        confirmLabel={t('automation.runs.cancel')}
        cancelLabel={t('automation.runs.keepGoing')}
        loading={cancel.isPending}
        onConfirm={onCancel}
      />
    </>
  );
}

const Meta = ({ icon, label, value }: { icon: ReactNode; label: string; value: string }) => (
  <div className="space-y-1">
    <dt className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">
      <span aria-hidden="true">{icon}</span>
      {label}
    </dt>
    <dd className="font-mono text-[13px] font-semibold text-ink">{value}</dd>
  </div>
);

/** The run ended and the report has no tests: GitLab is still building it, or the build failed before the tests. */
const NoReport = ({
  results,
  loading,
  error,
}: {
  results: AutomationRunResultsDto | undefined;
  loading: boolean;
  error: unknown;
}) => {
  const { t } = useT();
  const errorText = useRunsErrorText();
  if (results?.ready) return null;
  const text = error
    ? t('automation.runs.noReport.error', { error: errorText(error) })
    : loading || !results
      ? t('automation.runs.noReport.loading')
      : t('automation.runs.noReport.empty');
  return (
    <Card>
      <CardBody className="flex items-start gap-2.5 text-[13px] text-ink-muted">
        <FileWarning className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <span>{text}</span>
      </CardBody>
    </Card>
  );
};

const LEVEL_STYLES: Record<AutomationLogLineDto['level'], string> = {
  INFO: 'text-ink-muted',
  ERROR: 'text-fail',
  WARN: 'text-block',
  DEBUG: 'text-ink-subtle',
  DONE: 'text-pass',
};

const LogViewer = ({ logs, live }: { logs: AutomationLogLineDto[]; live: boolean }) => {
  const { t } = useT();
  const ref = useRef<HTMLDivElement>(null);
  const [autoscroll, setAutoscroll] = useState(true);
  useEffect(() => {
    if (autoscroll && ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [logs, autoscroll]);

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <h2 className="text-[14px] font-semibold text-ink">{t('automation.runs.log')}</h2>
          {live ? (
            <Badge className="bg-accent-soft text-accent-fg">
              <span className="size-1.5 animate-pulse rounded-full bg-accent" aria-hidden="true" />
              {t('automation.runs.live')}
            </Badge>
          ) : null}
        </div>
        <label className="flex cursor-pointer items-center gap-1.5 text-[12px] text-ink-muted">
          <input
            type="checkbox"
            checked={autoscroll}
            onChange={(event) => setAutoscroll(event.target.checked)}
            className="accent-accent"
          />
          {t('automation.runs.autoscroll')}
        </label>
      </div>
      <div
        ref={ref}
        className="max-h-[380px] overflow-auto bg-sunken p-4 font-mono text-[12.5px] leading-relaxed"
      >
        {logs.length === 0 ? (
          <span className="text-ink-subtle">{t('automation.runs.waitingLog')}</span>
        ) : null}
        {logs.map((line, index) => (
          <div key={index} className="flex items-baseline gap-2">
            <span className="shrink-0 text-ink-subtle">{line.ts}</span>
            <span className={cn('w-14 shrink-0 font-semibold', LEVEL_STYLES[line.level])}>
              [{line.level}]
            </span>
            <span
              className={cn(
                'min-w-0 whitespace-pre-wrap break-words',
                line.level === 'ERROR' ? 'text-fail' : 'text-ink',
              )}
            >
              {line.text}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
};
